/**
 * Cloud OAuth PKCE Service
 *
 * Implements Authorization Code flow with PKCE for desktop (Electron),
 * mobile (Capacitor), and web apps.
 *
 * Flow:
 * 1. Generate PKCE code_verifier + code_challenge (SHA-256)
 * 2. Open system browser to /api/oauth2/authorize?...
 * 3. User logs in on otto-web → redirected back to app via custom scheme/URL
 * 4. App receives authorization code
 * 5. Exchange code for access_token + refresh_token at /api/oauth2/token
 */

import { getRuntimeConfig, isElectron, isCapacitor } from '../config/runtime';
import { errorMessage } from '../utils/errors';

const OAUTH_STATE_KEY = 'otto_oauth_state';
const OAUTH_CODE_VERIFIER_KEY = 'otto_oauth_code_verifier';
const OAUTH_PENDING_KEY = 'otto_oauth_pending';

/**
 * Generate a cryptographically random string for PKCE
 */
function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Base64URL-encode an ArrayBuffer (no padding)
 */
function base64URLEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Generate PKCE code verifier and challenge (SHA-256)
 */
async function generatePKCE() {
  const codeVerifier = generateRandomString(64); // 128 hex chars

  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const codeChallenge = base64URLEncode(digest);

  return { codeVerifier, codeChallenge };
}

/**
 * Get the OAuth callback URL for the current platform.
 * For Electron, uses a localhost URL instead of custom protocol
 * because Windows protocol registration is unreliable in dev mode.
 */
function getCallbackUrl(): string {
  if (isElectron()) {
    // Use the localhost origin — works immediately with Vite dev server
    // or any local web server serving the app.
    const origin = (typeof window !== 'undefined' && window.location.origin) || 'http://localhost:3000';
    // If loading from file://, fall back to localhost
    if (origin.startsWith('file://')) {
      return 'http://localhost:3000/auth/callback';
    }
    return `${origin}/auth/callback`;
  }
  if (isCapacitor()) {
    // Capacitor: app scheme for deep linking
    return 'com.ottohome.app://oauth/callback';
  }
  // Web: same-origin callback page
  return `${window.location.origin}/auth/callback`;
}

/**
 * Initiate cloud OAuth login via PKCE.
 * Generates PKCE params, opens browser to authorize endpoint.
 */
export async function startCloudOAuthLogin(): Promise<{ success: boolean; error?: string }> {
  try {
    const config = getRuntimeConfig();
    const cloudBase = config.cloudApiUrl || 'https://torik.ai/api';

    // Generate PKCE
    const { codeVerifier, codeChallenge } = await generatePKCE();
    const state = generateRandomString(16);

    // Store PKCE data locally for callback
    localStorage.setItem(OAUTH_STATE_KEY, state);
    localStorage.setItem(OAUTH_CODE_VERIFIER_KEY, codeVerifier);
    localStorage.setItem(OAUTH_PENDING_KEY, 'true');

    // Build authorization URL
    const clientId = isElectron() ? 'otto-electron' : isCapacitor() ? 'otto-android' : 'otto-ui';

    let redirectUri = getCallbackUrl();

    // Electron: start a local HTTP server to receive the callback,
    // then use http://127.0.0.1:PORT/auth/callback as redirect_uri.
    if (isElectron()) {
      const electron = window.electron;
      if (electron?.startOAuthServer) {
        try {
          const port = await electron.startOAuthServer();
          redirectUri = `http://127.0.0.1:${port}/auth/callback`;
          console.log('[CloudOAuth] Electron OAuth server on port', port);
        } catch (err) {
          console.error('[CloudOAuth] Failed to start OAuth server:', err);
          // Fall back to localhost:3000
          redirectUri = 'http://localhost:3000/auth/callback';
        }
      }
    }

    const authUrl = new URL(`${cloudBase}/oauth2/authorize`);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('client_id', clientId);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('code_challenge', codeChallenge);
    authUrl.searchParams.set('code_challenge_method', 'S256');
    authUrl.searchParams.set('state', state);
    authUrl.searchParams.set('scope', 'openid profile email');

    console.log('[CloudOAuth] Opening auth URL:', authUrl.toString());

    if (isElectron()) {
      // Electron: open in default browser
      const electron = window.electron;
      if (electron?.openExternalUrl) {
        await electron.openExternalUrl(authUrl.toString());
      } else {
        window.open(authUrl.toString(), '_blank');
      }
    } else if (isCapacitor()) {
      // Capacitor: use Browser plugin or system browser
      try {
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: authUrl.toString() });
      } catch {
        // Fallback: open in system browser
        window.open(authUrl.toString(), '_system');
      }
    } else {
      // Web: navigate directly
      window.location.href = authUrl.toString();
    }

    return { success: true };
  } catch (error) {
    console.error('[CloudOAuth] Failed to start OAuth:', error);
    return { success: false, error: errorMessage(error, 'Failed to start OAuth login') };
  }
}

/**
 * Handle the OAuth redirect callback.
 * Extracts the authorization code from the URL, validates state,
 * and exchanges it for tokens.
 */
export async function handleCloudOAuthCallback(
  callbackUrl: string,
): Promise<{ success: boolean; accessToken?: string; refreshToken?: string; error?: string }> {
  try {
    // Parse callback URL
    const url = new URL(callbackUrl.replace(/^otto:\/\//, 'https://torik.ai/'));
    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');
    const errorParam = url.searchParams.get('error');

    if (errorParam) {
      const errorDesc = url.searchParams.get('error_description') || errorParam;
      return { success: false, error: errorDesc };
    }

    if (!code) {
      return { success: false, error: 'No authorization code in callback' };
    }

    // Validate state
    const savedState = localStorage.getItem(OAUTH_STATE_KEY);
    if (state && savedState && state !== savedState) {
      console.error('[CloudOAuth] State mismatch — possible CSRF attack');
      return { success: false, error: 'State mismatch' };
    }

    // Get stored code verifier
    const codeVerifier = localStorage.getItem(OAUTH_CODE_VERIFIER_KEY);
    if (!codeVerifier) {
      return { success: false, error: 'Missing PKCE code verifier' };
    }

    // Exchange code for tokens
    const config = getRuntimeConfig();
    const cloudBase = config.cloudApiUrl || 'https://torik.ai/api';
    const tokenUrl = `${cloudBase}/oauth2/token`;

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        grant_type: 'authorization_code',
        code,
        code_verifier: codeVerifier,
      }),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errBody?.error || `Token exchange failed: ${response.status}`,
      };
    }

    const data = await response.json();

    // Clean up PKCE state
    localStorage.removeItem(OAUTH_STATE_KEY);
    localStorage.removeItem(OAUTH_CODE_VERIFIER_KEY);
    localStorage.removeItem(OAUTH_PENDING_KEY);

    return {
      success: true,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    };
  } catch (error) {
    console.error('[CloudOAuth] Callback processing failed:', error);
    return { success: false, error: errorMessage(error, 'OAuth callback failed') };
  }
}

/**
 * Check if there's a pending OAuth flow (e.g., app was closed and reopened)
 */
export function hasPendingOAuth(): boolean {
  return localStorage.getItem(OAUTH_PENDING_KEY) === 'true';
}

/**
 * Clear any pending OAuth state
 */
export function clearOAuthState(): void {
  localStorage.removeItem(OAUTH_STATE_KEY);
  localStorage.removeItem(OAUTH_CODE_VERIFIER_KEY);
  localStorage.removeItem(OAUTH_PENDING_KEY);
}
